import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useUser } from '../contexts/UserContext'
import { useWorkOrderContext } from '../contexts/WorkOrderContext'
import { type TagFilters, tagService } from '../services/tagService'
import type { Tag, TagStatus } from '../types/tag'
import type { CreateTagData } from '../types/tag'
import type { ApiTag } from '../types/tag'
import type { WorkOrder } from '../types/workorder'

export function useTags(filters?: TagFilters) {
  const [tags, setTags] = useState<Tag[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const { tagId: urlTagId } = useParams()
  const { user } = useUser()
  const loadingRef = useRef(false) // To prevent concurrent loads
  const { workOrders } = useWorkOrderContext()

  const getCurrentTag = useCallback(
    (tagId?: string) => {
      const found = tags.find((tag) => String(tag.id) === tagId)
      return found
    },
    [tags],
  )

  const loadTags = useCallback(
    async (force = false, currentFilters?: TagFilters) => {
      // Use provided filters or fall back to hook-level filters
      const filtersToUse = currentFilters || filters

      // Prevent concurrent calls and redundant loads (but allow force reloads or when filters change)
      if (loadingRef.current && !force) {
        return
      }

      // Skip load if we have tags and no filters (unless force is true)
      if (!force && tags.length > 0 && !filtersToUse) {
        return
      }

      try {
        if (user) {
          loadingRef.current = true
          setIsLoading(true)
          const fetched = await tagService.getTags(filtersToUse as TagFilters)
          const maybeArray = fetched as unknown as Tag[] | { items?: Tag[] }
          const nextTags = Array.isArray(maybeArray)
            ? (maybeArray as Tag[])
            : (maybeArray as { items?: Tag[] }).items || []
          setTags(nextTags)
        } else {
          setTags([])
        }
      } catch (err) {
        console.error('Error loading tags:', err)
        setError(err instanceof Error ? err.message : 'Failed to load tags')
      } finally {
        setIsLoading(false)
        loadingRef.current = false
      }
    },
    [user, filters, tags.length],
  )

  const refreshTag = useCallback(
    async (tagId: string) => {
      try {
        // Instead of fetching the tag, we'll refresh the entire tag list
        // This ensures we have the latest data without relying on the single tag endpoint
        await loadTags(true)
      } catch (error) {
        console.error('Failed to refresh tag:', error)
        throw error
      }
    },
    [loadTags],
  )

  const loadCreatedTags = useCallback(
    async (force = false) => {
      if (loadingRef.current && !force) {
        return
      }

      try {
        if (user) {
          loadingRef.current = true
          setIsLoading(true)
          const fetched = await tagService.getCreatedTags()
          const maybeArray = fetched as unknown as Tag[] | { items?: Tag[] }
          const nextTags = Array.isArray(maybeArray)
            ? (maybeArray as Tag[])
            : (maybeArray as { items?: Tag[] }).items || []
          console.log('Created tags fetched:', nextTags)
          setTags(nextTags)
        } else {
          setTags([])
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load created tags',
        )
      } finally {
        setIsLoading(false)
        loadingRef.current = false
      }
    },
    [user],
  )

  // Combine the effects into one
  useEffect(() => {
    // Initial load when user is available
    if (user) {
      loadTags()
    }

    // Setup online/offline handlers
    const handleOnline = () => {
      setIsOffline(false)
      loadTags(true) // Force reload when coming back online
    }

    const handleOffline = () => setIsOffline(true)

    const handleSync = (event: MessageEvent) => {
      if (event.data?.type === 'SYNC_COMPLETE') {
        loadTags(true) // Force reload after sync
      }
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSync)
    }

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSync)
      }
    }
  }, [user, loadTags])

  const updateTagStatus = async (tagId: string, status: TagStatus) => {
    try {
      // Optimistic update
      setTags((prevTags) =>
        prevTags.map((tag) =>
          String(tag.id) === tagId ? { ...tag, status } : tag,
        ),
      )

      await tagService.updateTagStatus(tagId, status)
    } catch (error) {
      if (!navigator.onLine) {
        // Workbox will handle the queuing
        console.log('Update queued for background sync')
      } else {
        console.error('Failed to update tag status:', error)
        // Revert optimistic update
        await loadTags()
      }
      throw error
    }
  }

  const completeTagWithSignature = async (
    tagId: string,
    signatureData: string,
  ) => {
    try {
      // Optimistic update
      setTags((prevTags) =>
        prevTags.map((tag) =>
          String(tag.id) === tagId
            ? {
                ...tag,
                status: 'SIGNED' as TagStatus,
                supervisor_signature: signatureData,
              }
            : tag,
        ),
      )

      await tagService.updateTagSignature(tagId, {
        supervisor_signature_data: signatureData,
      })
      await tagService.updateTagStatus(tagId, 'SIGNED')
    } catch (error) {
      if (!navigator.onLine) {
        console.log(
          'Signature upload and status update queued for background sync',
        )
      } else {
        console.error('Failed to complete tag with signature:', error)
        await loadTags() // Revert optimistic update
      }
      throw error
    }
  }

  const submitDriverSignature = async (
    tagId: string,
    signatureData: string,
  ) => {
    try {
      // Optimistic update
      setTags((prevTags) =>
        prevTags.map((tag) =>
          String(tag.id) === tagId
            ? {
                ...tag,
                status: 'COMPLETE' as TagStatus,
                driver_signature: signatureData,
              }
            : tag,
        ),
      )

      await tagService.updateTagSignature(tagId, {
        driver_signature_data: signatureData,
      })
      await tagService.updateTagStatus(tagId, 'COMPLETE')
    } catch (error) {
      if (!navigator.onLine) {
        console.log(
          'Driver signature upload and status update queued for background sync',
        )
      } else {
        console.error('Failed to submit driver signature:', error)
        await loadTags() // Revert optimistic update
      }
      throw error
    }
  }

  const createTag = async (tagData: CreateTagData): Promise<Tag> => {
    try {
      const createdTag = await tagService.createTag(tagData)
      await Promise.all([loadTags(true), loadCreatedTags(true)])
      return createdTag
    } catch (error) {
      console.error('Failed to create tag:', error)
      throw error
    }
  }

  return {
    tags,
    isLoading,
    error,
    isOffline,
    createTag,
    updateTagStatus,
    completeTagWithSignature,
    refreshTags: loadTags,
    refreshCreatedTags: loadCreatedTags,
    getCurrentTag,
    submitDriverSignature,
    refreshTag,
  }
}
